/**
 * @file Timer service — start/stop/pause/break, time log management
 * Timer persists in localStorage via pbf_active_timer.
 */

import { store, load } from '@state/store.js';
import { emit } from '@core/events.js';

/**
 * Start a new timer.
 */
export function startTimer(customerId, customerName, jobId, jobDesc) {
  const timer = {
    customerId,
    customerName,
    jobId,
    jobDesc,
    startTime: Date.now(),
    paused: false,
    pauseStart: null,
    totalPausedMs: 0,
    onBreak: false,
    breakStart: null,
    breakDuration: 0,
    breaks: [],
  };
  store('active_timer', timer);
  emit('timer:started', timer);
  return timer;
}

/**
 * Stop the active timer and create a time log entry.
 */
export function stopTimer() {
  const timer = getActiveTimer();
  if (!timer) return null;

  const now = Date.now();

  // Calculate total pause/break time
  let totalPausedMs = timer.totalPausedMs || 0;
  if (timer.paused && timer.pauseStart) {
    totalPausedMs += (now - timer.pauseStart);
  }

  const totalMs = Math.max(0, now - timer.startTime - totalPausedMs);

  const entry = {
    id: Date.now().toString(36),
    customerId: timer.customerId,
    customerName: timer.customerName,
    jobId: timer.jobId,
    jobDesc: timer.jobDesc,
    startTime: timer.startTime,
    endTime: now,
    duration: totalMs,
    breakDuration: timer.breakDuration || 0,
    notes: '',
    date: new Date().toISOString().split('T')[0],
    createdAt: new Date().toISOString(),
  };

  // Add to time logs
  const logs = load('time_logs', []);
  logs.unshift(entry);
  store('time_logs', logs);

  // Clear active timer
  store('active_timer', null);
  emit('timer:stopped', entry);
  return entry;
}

/**
 * Pause the active timer.
 */
export function pauseTimer() {
  const timer = getActiveTimer();
  if (!timer || timer.paused) return;
  timer.paused = true;
  timer.pauseStart = Date.now();
  timer.onBreak = false;
  store('active_timer', timer);
  emit('timer:paused', timer);
}

/**
 * Resume a paused timer.
 */
export function resumeTimer() {
  const timer = getActiveTimer();
  if (!timer || !timer.paused) return;
  if (timer.pauseStart) {
    const pausedDuration = Date.now() - timer.pauseStart;
    timer.totalPausedMs = (timer.totalPausedMs || 0) + pausedDuration;
  }
  timer.paused = false;
  timer.pauseStart = null;
  timer.onBreak = false;
  store('active_timer', timer);
  emit('timer:resumed', timer);
}

/**
 * Start a break (tracks break time separately).
 */
export function startBreak() {
  const timer = getActiveTimer();
  if (!timer || timer.paused) return;
  timer.paused = true;
  timer.onBreak = true;
  timer.pauseStart = Date.now();
  timer.breakStart = Date.now();
  if (!timer.breaks) timer.breaks = [];
  timer.breaks.push({ start: Date.now() });
  store('active_timer', timer);
  emit('timer:break-start', timer);
}

/**
 * End a break.
 */
export function endBreak() {
  const timer = getActiveTimer();
  if (!timer || !timer.paused) return;
  const now = Date.now();
  const pausedDuration = timer.pauseStart ? now - timer.pauseStart : 0;

  // Track total paused time (which includes break time)
  timer.totalPausedMs = (timer.totalPausedMs || 0) + pausedDuration;

  // Track break duration separately for display
  timer.breakDuration = (timer.breakDuration || 0) + pausedDuration;

  // Close the break entry
  if (timer.breaks && timer.breaks.length > 0) {
    const lastBreak = timer.breaks[timer.breaks.length - 1];
    if (!lastBreak.end) {
      lastBreak.end = now;
      lastBreak.duration = pausedDuration;
    }
  }

  timer.paused = false;
  timer.pauseStart = null;
  timer.onBreak = false;
  timer.breakStart = null;
  store('active_timer', timer);
  emit('timer:break-end', timer);
}

/**
 * Get the currently active timer (persists across refreshes via localStorage).
 */
export function getActiveTimer() {
  return load('active_timer', null);
}

/**
 * Get all time log entries.
 */
export function getTimeLogs() {
  return load('time_logs', []);
}

/**
 * Get today's time log entries.
 */
export function getTodaysTimeLogs() {
  const today = new Date().toISOString().split('T')[0];
  return getTimeLogs().filter(log => log.date === today);
}

/**
 * Format milliseconds to HH:MM:SS.
 */
export function formatElapsed(ms) {
  if (!ms || ms < 0) ms = 0;
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h < 10 ? '0' : ''}${h}:${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
}

/**
 * Convert milliseconds to decimal hours.
 */
export function msToHours(ms) {
  return Math.round((ms / 3600000) * 100) / 100;
}
